/**
 * LA MARCA DE SOFTFI.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  EL SÍMBOLO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Un nudo entrelazado: tres lazos que se cruzan y se sostienen entre sí, sin
 * principio ni final. Encaja con lo que hace Softfi mejor de lo que parece — el
 * inventario, la caja y las ventas no son tres módulos sueltos, son tres cosas
 * que solo funcionan sostenidas unas por otras.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ EL DE COLOR ES LA IDENTIDAD. EL MONOCROMO NO LO SUSTITUYE.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Hay dos versiones del logo y **no son intercambiables**:
 *
 *   · **El de color** (`icono-*.png`) es Softfi. Va en la página, en el login,
 *     en la barra lateral, en el icono de la aplicación. Es lo que la gente
 *     reconoce, y es lo que se defiende.
 *
 *   · **El monocromo** (`icono-mono-512.png`) NO es una variante estética: es
 *     para los sitios donde el color **no llega**. El recibo térmico imprime a
 *     un solo bit —cada punto es negro o no es nada— y un degradado sale como
 *     una mancha gris sucia. Ahí, y solo ahí, hace falta la silueta.
 *
 * Por eso `mono` es una opción y no el valor por defecto. Cambiar eso sería
 * cambiar la marca por una comodidad técnica.
 */

import { cx } from "./ui";

/** El de color: la identidad. */
const COLOR = "/icono-192.png";

/** La silueta, para pintarla de un solo color con una máscara. */
const SILUETA = "/icono-mono-512.png";

type Props = {
  tamano?: number;
  className?: string;
  /**
   * Pinta la silueta en el color del texto en vez del logo de color.
   *
   * ⚠️ Solo donde el color no sirva: sobre un fondo del propio color de la
   * marca, o en algo que se vaya a imprimir en una sola tinta. En cualquier
   * otro sitio va el de color.
   */
  mono?: boolean;
};

export function Logo({ tamano = 36, className, mono = false }: Props) {
  if (mono) {
    // Con máscara: el PNG aporta la forma y `currentColor` el color, igual que
    // un icono de texto. Un archivo sirve para cualquier color.
    return (
      <span
        className={cx("inline-block shrink-0 select-none bg-current align-middle", className)}
        style={{
          width: tamano,
          height: tamano,
          WebkitMaskImage: `url(${SILUETA})`,
          maskImage: `url(${SILUETA})`,
          WebkitMaskSize: "contain",
          maskSize: "contain",
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskPosition: "center",
          maskPosition: "center",
        }}
        aria-hidden
      />
    );
  }

  return (
    <img
      src={COLOR}
      alt=""
      width={tamano}
      height={tamano}
      className={cx("shrink-0 select-none object-contain", className)}
      style={{ width: tamano, height: tamano }}
      aria-hidden
      // `eager` y no `lazy`: está en la primera pantalla que se ve, y cargarlo
      // tarde produce un salto en la barra lateral justo al abrir.
      loading="eager"
      decoding="async"
    />
  );
}

/**
 * El símbolo con el nombre al lado.
 *
 * El nombre lleva `tracking` negativo: las fuentes del sistema vienen espaciadas
 * para leer párrafos, y una palabra suelta con ese espaciado se ve floja.
 */
export function LogoConNombre({
  tamano = 32,
  subtitulo,
  className,
}: {
  tamano?: number;
  subtitulo?: string;
  className?: string;
}) {
  return (
    <div className={cx("flex min-w-0 items-center gap-2.5", className)}>
      <Logo tamano={tamano} />
      <div className="min-w-0">
        <p
          className="font-semibold leading-tight text-ink"
          style={{ fontSize: tamano * 0.46, letterSpacing: "-0.02em" }}
        >
          Softfi
        </p>
        {subtitulo && (
          <p className="truncate text-xs leading-tight text-muted">{subtitulo}</p>
        )}
      </div>
    </div>
  );
}
